/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { useTranslation } from 'react-i18next';
import { api } from '@/api';
import type { VnZoneRule } from '@/api/areas/declarations';
import { Badge } from '@/components/Badge';
import { Callout, DataTable, Section, type Column } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { KeyValue } from '@/components/KeyValue';
import { PageHeader, useDocumentTitle } from '@/components/PageHeader';
import { Loading } from '@/components/States';
import { useLanguage } from '@/i18n';
import { useApi } from '@/lib/useApi';

function useRuleText() {
  const { t } = useTranslation('declarations');
  const lang = useLanguage();
  return {
    types: (r: VnZoneRule) => r.registrant_types.map((x) => t(`vnZones.type.${x}` as 'vnZones.type.INDIVIDUAL')).join(', '),
    promo: (r: VnZoneRule) =>
      r.promo_code === 'none' || !r.promo
        ? t('vnZones.promoNone')
        : `${t(`vnZones.promo.${r.promo_code}` as 'vnZones.promo.eligible')} — ${lang === 'vi' ? r.promo.eligibility_vi : r.promo.eligibility_en}`,
    note: (r: VnZoneRule) => (lang === 'vi' ? r.note_vi : r.note_en),
    docs: (r: VnZoneRule) => r.documents.map((d) => (lang === 'vi' ? d.name_vi : d.name_en)).join('; '),
  };
}

function Confirmed({ rule }: { rule: VnZoneRule }) {
  const { t } = useTranslation('declarations');
  return rule.confirmed ? <Badge tone="ok">{t('vnZones.confirmed')}</Badge> : <Badge tone="warn">{t('vnZones.unconfirmed')}</Badge>;
}

/** The "Thông tin" tab of a .vn TLD: the zone's rule from config/vn-zones.yaml (read-only). */
export function VnZoneRuleSection({ zone }: { zone: string }) {
  const { t } = useTranslation('declarations');
  const text = useRuleText();
  const result = useApi((signal) => api.getVnZone(zone, { signal }), [zone]);
  if (result.status === 'loading') return <Loading rows={2} />;
  if (result.status === 'error') return <DataError error={result.error} onRetry={result.reload} title={t('vnZones.loadError')} />;
  const { rule, listed } = result.data;
  const note = text.note(rule);
  return (
    <Section title={t('vnZones.ruleTitle')} description={listed ? t('vnZones.fileDefined') : t('vnZones.defaultsApply')} actions={<Confirmed rule={rule} />}>
      <KeyValue
        columns={2}
        rows={[
          [t('vnZones.registrantTypes'), text.types(rule)],
          [t('vnZones.nationalities'), rule.nationalities.length ? rule.nationalities.join(', ') : t('vnZones.any')],
          [t('vnZones.manualActivation'), rule.manual_activation ? <Badge key="m" tone="warn">{t('vnZones.manual')}</Badge> : t('vnZones.automatic')],
          [t('vnZones.promoCode'), text.promo(rule)],
          [t('vnZones.documents'), text.docs(rule) || t('vnZones.formOnly')],
          [t('vnZones.source'), <span key="s" className="text-xs">{rule.source_reference}</span>],
        ]}
      />
      {note ? <Callout tone="info" className="mt-3">{note}</Callout> : null}
    </Section>
  );
}

/** Cài đặt › Tên miền & TLD › Quy định theo đuôi .vn: every rule of config/vn-zones.yaml. */
export function VnZonesPage() {
  const { t } = useTranslation('declarations');
  useDocumentTitle(t('vnZones.title'));
  const text = useRuleText();
  const result = useApi((signal) => api.listVnZones({ signal }), []);
  const columns: Column<VnZoneRule>[] = [
    { key: 'zone', header: t('vnZones.zone'), cell: (r) => <code className="text-sm font-semibold">{r.zone ? `.${r.zone}` : t('vnZones.defaults')}</code> },
    { key: 'types', header: t('vnZones.registrantTypes'), cell: (r) => <span className="text-xs">{text.types(r)}{r.nationalities.length ? ` (${r.nationalities.join(', ')})` : ''}</span> },
    { key: 'manual', header: t('vnZones.manualActivation'), cell: (r) => (r.manual_activation ? <Badge tone="warn">{t('vnZones.manual')}</Badge> : '—') },
    { key: 'promo', header: t('vnZones.promoCode'), cell: (r) => (r.promo_code === 'none' ? '—' : <Badge tone="info">{t(`vnZones.promo.${r.promo_code}` as 'vnZones.promo.eligible')}</Badge>) },
    { key: 'docs', header: t('vnZones.documents'), cell: (r) => <span className="text-xs">{text.docs(r) || '—'}</span> },
    {
      key: 'source',
      header: t('vnZones.source'),
      cell: (r) => (
        <span className="grid gap-1 text-xs">
          <span>{r.source_reference}</span>
          {text.note(r) ? <span className="text-muted-foreground">{text.note(r)}</span> : null}
        </span>
      ),
    },
    { key: 'confirmed', header: t('vnZones.status'), cell: (r) => <Confirmed rule={r} /> },
  ];
  return (
    <>
      <PageHeader title={t('vnZones.title')} subtitle={t('vnZones.description')} />
      {result.status === 'loading' ? <Loading rows={6} /> : null}
      {result.status === 'error' ? <DataError error={result.error} onRetry={result.reload} title={t('vnZones.loadError')} /> : null}
      {result.status === 'success' ? (
        <div className="grid min-w-0 grid-cols-1 gap-4">
          <Callout tone="info">{t('vnZones.fileNote', { version: result.data.version, source: result.data.source })}</Callout>
          {result.data.unconfirmed ? <Callout tone="warn">{t('vnZones.unconfirmedCount', { count: result.data.unconfirmed })}</Callout> : null}
          <Section flush>
            <DataTable columns={columns} rows={[...result.data.zones, result.data.defaults]} rowKey={(r) => r.zone || '(defaults)'} />
          </Section>
        </div>
      ) : null}
    </>
  );
}
